import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect } from 'react';
import {
    Card, Row, Col, Button, Typography, Space, Tag, List, Divider,
    message, Modal, Spin, Alert, Segmented
} from 'antd';
import {
    CheckOutlined, CloseOutlined, RocketOutlined, CrownOutlined,
    MailOutlined, TeamOutlined, ArrowLeftOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { billingService } from '../../services/billingService';
import { cartService } from '../../services/cartService';
import apiClient from '../../../shared/services/apiClient';

import { userPath } from '../../../shared/config/paths';
import AddonPanel from '../../../shared/components/AddonPanel';
import { addRenewalToCart } from '../../../shared/components/RenewAction';
import { formatMoney, PLATFORM_CURRENCY } from '../../../shared/utils/money';

const { Title, Text, Paragraph } = Typography;

const allBillingCycles = [
    { label: '月付', value: 'monthly', multiplier: 1 },
    { label: '季付', value: 'quarterly', multiplier: 3 },
    { label: '半年付', value: 'semi_annually', multiplier: 6 },
    { label: '年付', value: 'annually', multiplier: 12 },
];

const billingCycleLabels = {
    monthly: '月', quarterly: '季', semi_annually: '半年', annually: '年',
};

const YeapmailPlans = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const domainId = searchParams.get('domain_id') ? parseInt(searchParams.get('domain_id'), 10) : null;

    const [loading, setLoading] = useState(true);
    const [currentSubscription, setCurrentSubscription] = useState(null);
    const [availableUpgrades, setAvailableUpgrades] = useState([]);
    const [downgrades, setDowngrades] = useState([]);
    const [scheduledDown, setScheduledDown] = useState(null);
    const [renewInfo, setRenewInfo] = useState(null);
    const [upgrading, setUpgrading] = useState(false);
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [pricingMap, setPricingMap] = useState({});
    const [productIds, setProductIds] = useState({});   // slug → product id
    const [isFree, setIsFree] = useState(false);

    // Yeapmail 套餐数据
    const yeapmailPlans = [
        {
            id: null,
            slug: 'yeapmail-personal',
            name: '域名邮箱 个人版',
            icon: <MailOutlined />,
            color: '#1890ff',
            features: {
                mail_users_limit: { value: 5, label: '邮箱用户数', unit: '个' },
                mail_aliases_limit: { value: 10, label: '邮箱别名数', unit: '个' },
                mail_storage_gb: { value: 5, label: '存储空间', unit: 'GB' },
                mail_attachment_size_mb: { value: 25, label: '附件大小限制', unit: 'MB' },
                mail_daily_send_limit: { value: 100, label: '每日发送限制', unit: '封' },
                mail_spam_filter: { value: true, label: '垃圾邮件过滤' },
                mail_webmail: { value: true, label: 'Webmail访问' },
                mail_imap_pop: { value: true, label: 'IMAP/POP3' },
                mail_smtp_relay: { value: false, label: 'SMTP转发（即将推出）' },
                mail_catch_all: { value: false, label: '全收邮箱' },
                mail_api_access: { value: false, label: 'API访问（即将推出）' },
                mail_dkim_dmarc: { value: true, label: 'DKIM/DMARC' }
            },
            highlights: ['适合个人用户', '基础邮件功能', '快速上手'],
            recommended: false
        },
        {
            id: null,
            slug: 'yeapmail-business',
            name: '域名邮箱 企业版',
            icon: <TeamOutlined />,
            color: '#52c41a',
            features: {
                mail_users_limit: { value: 50, label: '邮箱用户数', unit: '个' },
                mail_aliases_limit: { value: 100, label: '邮箱别名数', unit: '个' },
                mail_storage_gb: { value: 50, label: '存储空间', unit: 'GB' },
                mail_attachment_size_mb: { value: 50, label: '附件大小限制', unit: 'MB' },
                mail_daily_send_limit: { value: 1000, label: '每日发送限制', unit: '封' },
                mail_spam_filter: { value: true, label: '垃圾邮件过滤' },
                mail_webmail: { value: true, label: 'Webmail访问' },
                mail_imap_pop: { value: true, label: 'IMAP/POP3' },
                mail_smtp_relay: { value: true, label: 'SMTP转发（即将推出）' },
                mail_catch_all: { value: true, label: '全收邮箱' },
                mail_api_access: { value: true, label: 'API访问（即将推出）' },
                mail_dkim_dmarc: { value: true, label: 'DKIM/DMARC' }
            },
            highlights: ['适合中小企业', '完整邮件功能', 'API集成'],
            recommended: true
        },
        {
            id: null,
            slug: 'yeapmail-enterprise',
            name: '域名邮箱 旗舰版',
            icon: <CrownOutlined />,
            color: '#faad14',
            features: {
                mail_users_limit: { value: -1, label: '邮箱用户数', unit: '个', display: '无限制' },
                mail_aliases_limit: { value: -1, label: '邮箱别名数', unit: '个', display: '无限制' },
                mail_storage_gb: { value: 500, label: '存储空间', unit: 'GB' },
                mail_attachment_size_mb: { value: 100, label: '附件大小限制', unit: 'MB' },
                mail_daily_send_limit: { value: -1, label: '每日发送限制', unit: '封', display: '无限制' },
                mail_spam_filter: { value: true, label: '垃圾邮件过滤' },
                mail_webmail: { value: true, label: 'Webmail访问' },
                mail_imap_pop: { value: true, label: 'IMAP/POP3' },
                mail_smtp_relay: { value: true, label: 'SMTP转发（即将推出）' },
                mail_catch_all: { value: true, label: '全收邮箱' },
                mail_api_access: { value: true, label: 'API访问（即将推出）' },
                mail_dkim_dmarc: { value: true, label: 'DKIM/DMARC' }
            },
            highlights: ['适合大型企业', '无限用户', '专属技术支持'],
            recommended: false
        }
    ].map((p) => ({ ...p, id: productIds[p.slug] || null }));

    // Compute available billing cycles from pricing data
    const availableCycles = (() => {
        const cycles = allBillingCycles.filter(c => c.value !== 'onetime');
        if (Object.keys(pricingMap).length === 0) return cycles.filter(c => c.value === 'monthly');
        const paidProductIds = Object.keys(pricingMap).map(Number);
        if (paidProductIds.length === 0) return cycles.filter(c => c.value === 'monthly');
        return cycles.filter(c =>
            paidProductIds.some(pid => pricingMap[pid]?.[c.value] !== undefined)
        );
    })();

    // 套餐定价币种（价格全部来自 /products 定价，记录自带币种）
    const planCurrency = (plan) => pricingMap[plan?.id]?._currency || PLATFORM_CURRENCY;
    const monthlyPrice = (plan) => getRealPrice(plan?.id, 'monthly');

    const getRealPrice = (productId, cycle) => {
        return pricingMap[productId]?.[cycle];
    };

    const getDiscountPercent = (productId, cycle) => {
        const monthlyPrice = getRealPrice(productId, 'monthly');
        const cyclePrice = getRealPrice(productId, cycle);
        if (!monthlyPrice || !cyclePrice || cycle === 'monthly') return 0;
        const multiplier = allBillingCycles.find(c => c.value === cycle)?.multiplier || 1;
        const fullPrice = monthlyPrice * multiplier;
        if (fullPrice <= 0) return 0;
        return Math.round((1 - cyclePrice / fullPrice) * 100);
    };

    const getMaxDiscount = (cycle) => {
        let maxDiscount = 0;
        Object.keys(pricingMap).forEach(pid => {
            const d = getDiscountPercent(Number(pid), cycle);
            if (d > maxDiscount) maxDiscount = d;
        });
        return maxDiscount;
    };

    const getPriceDisplay = (plan) => {
        let cycle = billingCycle;
        let price = getRealPrice(plan.id, cycle);
        if (price === undefined) { cycle = 'monthly'; price = monthlyPrice(plan); }
        if (price === undefined) return '-';
        if (price === 0) return t('Free');
        return `${formatMoney(price, planCurrency(plan), { decimals: Number.isInteger(price) ? 0 : 2 })}/${t(billingCycleLabels[cycle])}`;
    };

    const getOriginalPrice = (plan, cycle) => {
        const multiplier = allBillingCycles.find(c => c.value === cycle)?.multiplier || 1;
        return (monthlyPrice(plan) || 0) * multiplier;
    };

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            // 获取真实定价数据
            try {
                const pricingResponse = await apiClient.get('/products', { params: { product_type: 'yeapmail', status: 'active' } });
                if (pricingResponse.data?.success && pricingResponse.data?.data) {
                    const map = {};
                    const ids = {};
                    pricingResponse.data.data.forEach(product => {
                        if (product.slug) ids[product.slug] = product.id;
                        if (product.pricing && Array.isArray(product.pricing)) {
                            map[product.id] = {};
                            product.pricing.forEach(p => {
                                if (p.enabled) {
                                    map[product.id][p.billing_cycle] = parseFloat(p.price);
                                    // 展示用：记录该产品定价自己的币种
                                    if (p.currency && !map[product.id]._currency) map[product.id]._currency = p.currency;
                                }
                            });
                        }
                    });
                    setPricingMap(map);
                    setProductIds(ids);
                }
            } catch (e) {
                // 定价拉取失败时价格显示为 -（不再有静态兜底价）
            }

            if (domainId) {
                // 获取该域名的当前套餐信息
                const subResponse = await billingService.getSubscriptionOverview('yeapmail', domainId);
                if (subResponse.success && subResponse.data.has_subscription) {
                    setCurrentSubscription(subResponse.data);
                }

                // 获取可升级套餐列表
                const upgradeResponse = await billingService.getAvailableUpgrades('yeapmail', domainId);
                if (upgradeResponse.success) {
                    setAvailableUpgrades(upgradeResponse.data.available_upgrades || []);
                    setDowngrades(upgradeResponse.data.downgrades || []);
                    setScheduledDown(upgradeResponse.data.scheduled_downgrade || null);
                    setIsFree(!!upgradeResponse.data.free);
                    setRenewInfo(upgradeResponse.data.renew_required
                        ? { subscriptionId: upgradeResponse.data.current_subscription_id, expiry: upgradeResponse.data.expiry_date }
                        : null);
                }
            }

        } catch (error) {
            message.error(t('Failed to load plan information: {{error}}', { error: error.message }));
        } finally {
            setLoading(false);
        }
    };

    const renewNow = () => {
        if (!renewInfo?.subscriptionId) return;
        addRenewalToCart({ subscriptionId: renewInfo.subscriptionId, navigate, t });
    };

    const cartAdded = () => {
        window.dispatchEvent(new CustomEvent('cart:updated'));
        navigate(userPath('/billing/cart'));
    };
    const errText = (e, fallback) => {
        if (e?.code === 'PLAN_CONFLICT' && Array.isArray(e.conflicts) && e.conflicts.length) {
            return `${e.error || e.message || fallback}：${e.conflicts.map((c) => (typeof c === 'string' ? c : (c.message || c.name || JSON.stringify(c)))).join('；')}`;
        }
        return e?.error || e?.message || fallback;
    };
    // 升级 / 降级：加入购物车，付款后生效
    const addChange = async (productId, cycle) => {
        setUpgrading(true);
        try {
            const r = await cartService.addChange({ subscription_id: currentSubscription?.subscription_id, product_id: productId, ...(cycle ? { billing_cycle: cycle } : {}) });
            if (r.success) cartAdded(); else message.error(errText(r, t('Operation failed')));
        } catch (e) {
            message.error(errText(e, t('Operation failed')));
        } finally {
            setUpgrading(false);
        }
    };

    // 降级：不中途退钱，续费单按低档计价，付款后生效（超限邮箱会被停用，先让用户确认）
    const confirmDowngrade = (plan, downOpt) => {
        Modal.confirm({
            title: t('Downgrade to {{name}}?', { name: t(plan.name) }),
            width: 480,
            content: (
                <div>
                    <p>{t('New plan price: {{price}}/{{cycle}}', { price: formatMoney(downOpt.price, downOpt.currency), cycle: t(billingCycleLabels[downOpt.billing_cycle] || downOpt.billing_cycle) })}</p>
                    <p>{downOpt.effective_at
                        ? t('Your current plan runs until {{date}}; the new plan applies after you pay the renewal.', { date: downOpt.effective_at.slice(0, 10) })
                        : t('The new plan applies right after you pay the renewal.')}</p>
                    {(downOpt.warnings || []).map((w) => (
                        <p key={w} style={{ color: '#d4380d', marginBottom: 4 }}>⚠ {w}</p>
                    ))}
                </div>
            ),
            okText: t('Add to cart'),
            cancelText: t('Cancel'),
            onOk: () => addChange(downOpt.product_id),
        });
    };

    const handleSelectPlan = async (plan) => {
        // 检查是否已经是当前套餐
        if (currentSubscription && currentSubscription.product_name === plan.name) {
            message.info(t('You are already using this plan'));
            return;
        }

        // 套餐已过期：先续费（续费从今天起算并恢复服务），续费后再升级
        if (currentSubscription && renewInfo) {
            Modal.confirm({
                title: t('Plan expired'),
                content: t('Renew your current plan first, then you can upgrade.'),
                okText: t('Renew now'),
                cancelText: t('Cancel'),
                onOk: renewNow,
            });
            return;
        }

        // 如果用户已有订阅，则提供升级选项
        if (currentSubscription) {
            const upgradeOption = availableUpgrades.find(u => u.name === plan.name);
            if (!upgradeOption) {
                const downOpt = downgrades.find(d => d.name === plan.name || Number(d.product_id) === plan.id);
                if (downOpt) {
                    confirmDowngrade(plan, downOpt);
                } else {
                    message.warning(t('This plan is not available for change'));
                }
                return;
            }

            // 免费 → 付费：按所选周期全价；付费 → 更高档：按剩余天数折算，周期不变
            const cyc = (isFree ? (upgradeOption.cycles || []).find((c) => c.billing_cycle === billingCycle) : null)
                || (upgradeOption.cycles || [])[0]
                || { billing_cycle: upgradeOption.billing_cycle, price: upgradeOption.price, cost: upgradeOption.upgrade_cost ?? upgradeOption.prorated_cost };
            const cur = cyc.currency || upgradeOption.currency || planCurrency(plan);
            Modal.confirm({
                title: t("Confirm upgrade to {{name}}", { name: t(plan.name) }),
                content: (
                    <div>
                        <p>{t('New plan price:')}<strong>{formatMoney(cyc.price, cur)}</strong>/{t(billingCycleLabels[cyc.billing_cycle] || cyc.billing_cycle)}</p>
                        <p>{t('Due now: ')}<strong>{formatMoney(cyc.cost ?? cyc.price, cur)}</strong>{!isFree && upgradeOption.days_remaining ? t('(prorated for {{days}} remaining days)', { days: upgradeOption.days_remaining }) : ''}</p>
                    </div>
                ),
                okText: t('Add to cart'),
                cancelText: t('Cancel'),
                onOk: () => addChange(upgradeOption.product_id, isFree ? cyc.billing_cycle : undefined),
            });
        } else {
            // 域名还没有套餐：按域名加入购物车
            if (!plan.id) { message.error(t('Plan unavailable')); return; }
            const cycle = getRealPrice(plan.id, billingCycle) !== undefined ? billingCycle : (Object.keys(pricingMap[plan.id] || {}).find((k) => k !== '_currency') || 'monthly');
            setUpgrading(true);
            try {
                const r = await cartService.addItem({ product_id: plan.id, billing_cycle: cycle, quantity: 1, resource_id: domainId });
                if (r.success) cartAdded(); else message.error(errText(r, t('Failed to add')));
            } catch (e) {
                message.error(errText(e, t('Failed to add')));
            } finally {
                setUpgrading(false);
            }
        }
    };

    const renderFeatureValue = (feature) => {
        if (typeof feature.value === 'boolean') {
            return feature.value ? (
                <CheckOutlined style={{ color: '#52c41a', fontSize: 18 }} />
            ) : (
                <CloseOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
            );
        }

        if (feature.value === -1) {
            return <Tag color="green">{t("Unlimited")}</Tag>;
        }

        return (
            <Text strong>
                {feature.display ? t(feature.display) : `${feature.value}${feature.unit ? t(feature.unit) : ''}`}
            </Text>
        );
    };

    const renderPlanCard = (plan) => {
        const isCurrentPlan = currentSubscription && currentSubscription.product_name === plan.name;

        return (
            <Col xs={24} sm={24} md={8} key={plan.slug}>
                <Card
                    hoverable
                    style={{
                        borderColor: isCurrentPlan ? plan.color : plan.recommended ? '#52c41a' : undefined,
                        borderWidth: isCurrentPlan || plan.recommended ? 2 : 1,
                        position: 'relative',
                        height: '100%'
                    }}
                    bodyStyle={{ paddingBottom: 24 }}
                >
                    {plan.recommended && !isCurrentPlan && (
                        <div
                            style={{
                                position: 'absolute',
                                top: -1,
                                right: 20,
                                background: '#52c41a',
                                color: 'white',
                                padding: '4px 16px',
                                borderRadius: '0 0 8px 8px',
                                fontSize: 12,
                                fontWeight: 'bold'
                            }}
                        >
                            {t("Recommended")}
                        </div>
                    )}

                    {isCurrentPlan && (
                        <div
                            style={{
                                position: 'absolute',
                                top: -1,
                                right: 20,
                                background: plan.color,
                                color: 'white',
                                padding: '4px 16px',
                                borderRadius: '0 0 8px 8px',
                                fontSize: 12,
                                fontWeight: 'bold'
                            }}
                        >
                            {t("Current plan")}
                        </div>
                    )}

                    <div style={{ textAlign: 'center', padding: '24px 0 16px' }}>
                        <div style={{ fontSize: 48, color: plan.color, marginBottom: 16 }}>
                            {plan.icon}
                        </div>
                        <Title level={3} style={{ marginBottom: 8 }}>
                            {t(plan.name)}
                        </Title>
                        <Title level={2} style={{ color: plan.color, marginBottom: 0 }}>
                            {getPriceDisplay(plan)}
                        </Title>
                        {billingCycle !== 'monthly' && monthlyPrice(plan) > 0 && getDiscountPercent(plan.id, billingCycle) > 0 && (
                            <Text type="secondary" delete style={{ fontSize: 14 }}>
                                {formatMoney(getOriginalPrice(plan, billingCycle), planCurrency(plan))}/{t(billingCycleLabels[billingCycle])}
                            </Text>
                        )}
                    </div>

                    <Divider />

                    <List
                        size="small"
                        dataSource={Object.entries(plan.features)}
                        renderItem={([key, feature]) => (
                            <List.Item style={{ padding: '8px 0', border: 'none' }}>
                                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                    <Text type="secondary">{t(feature.label)}</Text>
                                    {renderFeatureValue(feature)}
                                </Space>
                            </List.Item>
                        )}
                    />

                    <Divider />

                    <div style={{ marginBottom: 16 }}>
                        {plan.highlights.map((highlight, index) => (
                            <Tag key={index} color={plan.color} style={{ marginBottom: 8 }}>
                                {t(highlight)}
                            </Tag>
                        ))}
                    </div>

                    <Button
                        type={isCurrentPlan ? 'default' : 'primary'}
                        icon={<RocketOutlined />}
                        block
                        size="large"
                        disabled={isCurrentPlan || upgrading || !domainId}
                        onClick={() => handleSelectPlan(plan)}
                        style={{
                            background: isCurrentPlan ? undefined : plan.color,
                            borderColor: plan.color
                        }}
                    >
                        {isCurrentPlan ? t('Current plan') : (currentSubscription ? t('Upgrade now') : t('Select a plan'))}
                    </Button>
                </Card>
            </Col>
        );
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '100px 0' }}>
                <Spin size="large" />
                <p style={{ marginTop: 16 }}>{t("Loading plan information...")}</p>
            </div>
        );
    }

    if (!domainId) {
        return (
            <div>
                <Alert
                    message={t("Please select a domain first")}
                    type="warning"
                    showIcon
                    style={{ marginBottom: 24 }}
                    action={
                        <Button
                            type="primary"
                            icon={<ArrowLeftOutlined />}
                            onClick={() => navigate(userPath('/webmail/domains'))}
                        >
                            {t("Back to domains")}
                        </Button>
                    }
                />
            </div>
        );
    }

    return (
        <div>
            <div style={{ marginBottom: 24 }}>
                <Button
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate(userPath('/webmail/domains'))}
                    style={{ marginBottom: 16 }}
                >
                    {t("Back to domains")}
                </Button>
                <Title level={2}>{t("Domain email plans")}</Title>
                <Paragraph type="secondary">
                    {t("Choose a domain email plan that fits your business for professional business email")}
                </Paragraph>
            </div>

            {(!currentSubscription || isFree) && availableCycles.length > 1 && (
                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                    <Segmented
                        size="large"
                        options={availableCycles.map(o => {
                            const discount = getMaxDiscount(o.value);
                            return {
                                label: discount > 0
                                    ? <span>{t(o.label)} <Tag color="red" style={{ marginLeft: 4 }}>{t('Save {{discount}}%', { discount })}</Tag></span>
                                    : t(o.label),
                                value: o.value,
                            };
                        })}
                        value={billingCycle}
                        onChange={setBillingCycle}
                    />
                </div>
            )}

            {!currentSubscription && (
                <Alert
                    message={t("This domain has no plan subscription yet")}
                    type="info"
                    showIcon
                    style={{ marginBottom: 24 }}
                />
            )}

            {currentSubscription?.subscription_id && <AddonPanel subscriptionId={currentSubscription.subscription_id} />}
            {scheduledDown && (
                <Alert
                    type="info" showIcon style={{ marginBottom: 16 }}
                    message={t('Downgrade to {{name}} scheduled; applies when the renewal is paid', { name: scheduledDown.name })}
                    action={<Space>
                        {scheduledDown.invoice_id && <Button size="small" type="primary" onClick={() => navigate(userPath(`/billing/invoices/${scheduledDown.invoice_id}`))}>{t('Pay now')}</Button>}
                        <Button size="small" onClick={async () => {
                            const r = await billingService.cancelDowngrade(currentSubscription?.subscription_id);
                            if (r.success) { message.success(t('Downgrade cancelled')); loadData(); } else { message.error(r.error || t('Operation failed')); }
                        }}>{t('Cancel downgrade')}</Button>
                    </Space>}
                />
            )}
            {renewInfo && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={t('Your plan has expired; email service may be suspended')}
                    action={<Button type="primary" size="small" onClick={renewNow}>{t('Renew now')}</Button>}
                />
            )}
            <Row gutter={[24, 24]}>
                {yeapmailPlans.map(plan => renderPlanCard(plan))}
            </Row>

            <Card style={{ marginTop: 24 }} bordered={false}>
                <Title level={4}>{t("Feature description")}</Title>
                <Row gutter={[16, 16]}>
                    <Col span={12}>
                        <Space direction="vertical" size="small">
                            <Text><strong>{t("Mailboxes:")}</strong>{t("Number of mailboxes you can create")}</Text>
                            <Text><strong>{t("Email aliases:")}</strong>{t("Number of email aliases you can create")}</Text>
                            <Text><strong>{t("Storage:")}</strong>{t("Total mailbox storage")}</Text>
                            <Text><strong>{t("Attachment limit:")}</strong>{t("Maximum size per attachment")}</Text>
                            <Text><strong>{t("Sending limit:")}</strong>{t("Number of emails you can send per day")}</Text>
                        </Space>
                    </Col>
                    <Col span={12}>
                        <Space direction="vertical" size="small">
                            <Text><strong>{t("Spam filtering (coming soon):")}</strong>{t("Automatically filter spam")}</Text>
                            <Text><strong>{t('Webmail:')}</strong>{t("Webmail access")}</Text>
                            <Text><strong>{t('IMAP/POP3:')}</strong>{t("Supports sending and receiving email via email clients")}</Text>
                            <Text><strong>{t("SMTP forwarding (coming soon):")}</strong>{t("Supports sending email from applications")}</Text>
                            <Text><strong>{t("Catch-all mailbox (coming soon):")}</strong>{t("Receive all email sent to the domain")}</Text>
                            <Text><strong>{t('DKIM/DMARC:')}</strong>{t("Email authentication to improve deliverability")}</Text>
                        </Space>
                    </Col>
                </Row>
            </Card>
        </div>
    );
};

export default YeapmailPlans;
